// Modified by AI on 06/12/2026. Edit #1.
import React, {
    useContext, useEffect, useState, useCallback, useMemo,
} from 'react';
import { chain, get, isEmpty } from 'lodash';

import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { matchPath } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PositionContext from './PositionContext';
import AutoCampaignHttpService from '../api/AutoCampaignHttpService';
import NotificationContext from './NotificationContext';
import Logger from '../services/logger';
import { AUTO_CAMPAIGN_TYPES } from '../modules/employer/position/candidates/AutoCampaignSwitch/constants';
import { API_RESPONSE_STATUSES } from '../constants/APIResponseStatusConfig';
import { RoutePaths } from '../constants/RoutePaths';
import { QUERY_KEYS, FIVE_MINUTES_MS } from '../constants/queryKeys';

export const AutoCampaignStatus = {
    NON_STARTED: 'nonStarted',
    PENDING_ACTIVATION: 'pendingActivation',
    ACTIVE: 'active',
    PAUSED: 'paused',
    ENDED: 'ended',
};

export const AutoCampaignSwitchOnStatuses = [
    AutoCampaignStatus.PENDING_ACTIVATION,
    AutoCampaignStatus.ACTIVE,
];

export const AutoCampaignSwitchOffStatuses = [
    AutoCampaignStatus.PAUSED,
    AutoCampaignStatus.ENDED,
    AutoCampaignStatus.NON_STARTED,
];

export const AutoCampaignActivatedStatuses = [
    AutoCampaignStatus.ACTIVE,
    AutoCampaignStatus.PAUSED,
];

const getCurrentStatus = (autoCampaign) => autoCampaign?.status?.current;
const getIsPaused = (autoCampaign) => getCurrentStatus(autoCampaign) === AutoCampaignStatus.PAUSED
    && Object.values(AUTO_CAMPAIGN_TYPES).includes(autoCampaign.autoCampaignType);
export const getIsSwitchedOn = (autoCampaign) => AutoCampaignSwitchOnStatuses.includes(getCurrentStatus(autoCampaign));
const getIsAutoCampaignActive = (autoCampaign) => getCurrentStatus(autoCampaign) === AutoCampaignStatus.ACTIVE;
const getIsAutoCampaignActivated = (autoCampaign) => AutoCampaignActivatedStatuses.includes(getCurrentStatus(autoCampaign));
export const getIsAutoCampaignEverStarted = (autoCampaign) => !!chain(autoCampaign)
    .get('status.history')
    .find((h) => AutoCampaignSwitchOnStatuses.includes(h?.status))
    .value();

const getTalentIdsFromManualEvents = (e) => chain(e).map('talentIds').flatten().value();

const getIsTalentsAlreadyInManualEventList = (eventsList, { talentIds: talentIdsFromNewEvent } = []) => {
    const talentsInManualEventList = getTalentIdsFromManualEvents(eventsList);
    const newTalentIds = talentIdsFromNewEvent.filter((talentIdFromNewEvent) => !talentsInManualEventList.includes(talentIdFromNewEvent));
    return isEmpty(newTalentIds);
};

const AutoCampaignContext = React.createContext({
    autoCampaign: {},
    isSwitchedOn: false,
    isAutoCampaignPaused: false,
    isAutoCampaignActive: false,
    switchAutoCampaign: () => {},
    registerManualEvent: () => {},
});

export const AutoCampaignProvider = AutoCampaignContext.Provider;

// Modified by AI on 06/12/2026. Edit #2.
export const AppWithAutoCampaign = ({ children }) => {
    const location = useLocation();
    const queryClient = useQueryClient();
    const { showErrorNotification } = useContext(NotificationContext);
    const { positionId } = useContext(PositionContext);

    const [loaded, setLoaded] = useState(false);

    // Modified by AI on 06/12/2026. Edit #3.
    // Modified by AI on 07/01/2026. Edit #4.
    // Fetch autoCampaign data
    const {
        data: autoCampaignRawData,
        error: autoCampaignError,
        isFetched,
        refetch: refetchAutoCampaign,
    } = useQuery({
        queryKey: QUERY_KEYS.autoCampaign(positionId),
        queryFn: () => AutoCampaignHttpService.getAutoCampaign(positionId),
        enabled: !!positionId,
        staleTime: FIVE_MINUTES_MS,
        gcTime: FIVE_MINUTES_MS,
    });

    // Destructuring default (= {}) only guards against undefined, not null.
    // BackendConnector.get() returns null when API result is null.
    const autoCampaign = autoCampaignRawData ?? {};

    // Error handling
    useEffect(() => {
        if (autoCampaignError) {
            Logger.error(autoCampaignError);
            showErrorNotification(autoCampaignError.message);
        }
    }, [autoCampaignError, showErrorNotification]);

    // Track loaded state
    useEffect(() => {
        setLoaded(!!positionId && isFetched);
    }, [positionId, isFetched]);

    const autoCampaignType = get(autoCampaign, 'autoCampaignType', '');
    const isAutoCampaignPaused = getIsPaused(autoCampaign);
    const isAutoCampaignOn = getIsSwitchedOn(autoCampaign);
    const isAutoCampaignActive = getIsAutoCampaignActive(autoCampaign);
    const isAutoCampaignActivated = getIsAutoCampaignActivated(autoCampaign);
    const manualEventList = autoCampaign.events?.current || [];
    const status = getCurrentStatus(autoCampaign);

    const isManualSelectionAutoCampaign = autoCampaignType === AUTO_CAMPAIGN_TYPES.HYBRID;
    const isManualMessageAutoCampaign = autoCampaignType === AUTO_CAMPAIGN_TYPES.MANUAL_MESSAGE;
    const isManualSelectionAutoCampaignActive = isManualSelectionAutoCampaign && isAutoCampaignActive;
    const isManualMessageAutoCampaignActive = isManualMessageAutoCampaign && isAutoCampaignActive;

    const refreshAutoCampaign = useCallback(async () => {
        await refetchAutoCampaign();
    }, [refetchAutoCampaign]);

    // Modified by AI on 06/12/2026. Edit #4.
    // Mutation for switching autoCampaign
    const switchAutoCampaignMutation = useMutation({
        mutationFn: (autoCampaignData) => {
            const match = matchPath(location.pathname, {
                path: RoutePaths.manualQualify,
                exact: false,
                strict: false,
            });

            const autoCampaignSwitchPayload = {
                ...autoCampaignData,
                isFromManualQualifySelection: !!match,
            };

            return AutoCampaignHttpService.switchAutoCampaign(positionId, autoCampaignSwitchPayload);
        },
        onSuccess: (data) => {
            queryClient.setQueryData(QUERY_KEYS.autoCampaign(positionId), data || {});
        },
        onError: (error) => {
            Logger.error(error);
            showErrorNotification(error.message);
        },
    });

    // Modified by AI on 06/12/2026. Edit #7.
    const switchAutoCampaign = useCallback(async (autoCampaignData) => {
        await switchAutoCampaignMutation.mutateAsync(autoCampaignData);
    }, [switchAutoCampaignMutation]);

    // Modified by AI on 06/12/2026. Edit #5.
    // Mutation for registering manual event
    const registerManualEventMutation = useMutation({
        mutationFn: (newEventParams) => AutoCampaignHttpService.registerManualEvent(positionId, newEventParams),
        onSuccess: (data) => {
            const isError = data?.status === API_RESPONSE_STATUSES.ERROR;
            if (isError) {
                showErrorNotification(data?.message);
                return;
            }
            queryClient.setQueryData(QUERY_KEYS.autoCampaign(positionId), data || {});
        },
        onError: (error) => {
            Logger.error(error);
            showErrorNotification(error.message);
        },
    });

    const registerManualEvent = useCallback(async (newEventParams = {}) => {
        const isTalentsInEventList = getIsTalentsAlreadyInManualEventList(manualEventList, newEventParams);
        let isEventAllowed = false;

        if (isAutoCampaignActivated || isTalentsInEventList) {
            isEventAllowed = true;
            return isEventAllowed;
        }

        try {
            const data = await registerManualEventMutation.mutateAsync(newEventParams);
            const isError = data?.status === API_RESPONSE_STATUSES.ERROR;

            if (isError) {
                isEventAllowed = false;
                return isEventAllowed;
            }

            isEventAllowed = true;
            return isEventAllowed;
        } catch (error) {
            isEventAllowed = false;
            return isEventAllowed;
        }
    }, [manualEventList, isAutoCampaignActivated, registerManualEventMutation]);

    const contextValue = useMemo(() => ({
        autoCampaign,
        isAutoCampaignOn,
        switchAutoCampaign,
        isAutoCampaignPaused,
        isAutoCampaignActive,
        autoCampaignType,
        isManualSelectionAutoCampaign,
        isManualMessageAutoCampaign,
        isManualSelectionAutoCampaignActive,
        isManualMessageAutoCampaignActive,
        registerManualEvent,
        refreshAutoCampaign,
        status,
        loaded,
    }), [
        autoCampaign,
        isAutoCampaignOn,
        switchAutoCampaign,
        isAutoCampaignPaused,
        isAutoCampaignActive,
        autoCampaignType,
        isManualSelectionAutoCampaign,
        isManualMessageAutoCampaign,
        isManualSelectionAutoCampaignActive,
        isManualMessageAutoCampaignActive,
        registerManualEvent,
        refreshAutoCampaign,
        status,
        loaded,
    ]);

    return <AutoCampaignProvider value={contextValue}>{children}</AutoCampaignProvider>;
};

AppWithAutoCampaign.propTypes = {
    children: PropTypes.node,
};

export default AutoCampaignContext;
